// Bolo2K client bootstrap. Connects to the server's /ws endpoint, decodes the
// welcome BMAP into a client-side ClientState, and runs an animation-frame
// render loop that also pushes input every frame.

import {
  MAP_SIZE,
  PILL_MAX_HEALTH,
  TERRAIN_MASK,
  Terrain,
  TREE_COST_BOAT,
  TREE_COST_NEW_PILL,
  TREE_COST_REPAIR_PILL,
} from '@bolo2k/shared';
import { BuildAction, type BuildActionCode, LGMStatus, TankKey } from '@bolo2k/sim';
import { Connection } from '@bolo2k/client-core';
import { applySnapshot, findMyTank, initFromWelcome, isFriendlyToMe, type ClientState } from '@bolo2k/client-core';
import { Renderer } from '@bolo2k/client-core';
import { Minimap } from '@bolo2k/client-core';
import { MiddleColumn } from './middleColumn.js';
import { ActionSidebar } from './actionSidebar.js';
import { RightColumn } from './rightColumn.js';
import { Keyboard } from './keyboard.js';
import { Interpolator } from '@bolo2k/client-core';
import { Predictor } from '@bolo2k/client-core';
import { BuildUI } from './buildUI.js';
import { AlliancePanel } from './alliancePanel.js';
import { ChatUI } from './chatUI.js';
import { Keyhint } from './keyhint.js';
import { NewsWire } from './newsWire.js';
import { WireEvents } from '@bolo2k/client-core';
import { showLobby } from './lobby.js';
import { loadSprites } from '@bolo2k/client-core';
import { Audio } from '@bolo2k/client-core';

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} not found`);
  return el;
}

// Status line — used only for transient state like the armed-action hint
// (BuildUI). Player-facing events (connection lifecycle, captures, kills)
// go to the news wire instead. The status line will be relocated to the
// middle-column action area in a later authentic-UI step.
// Called per-frame in some paths (e.g. the LGM-busy hint), so no-op when
// neither the text nor the class actually changed — a textContent WRITE
// invalidates render state even when the string is identical, a read does
// not. Compared against the live element (not a cached string) because
// BuildUI.refreshStatus() writes this same element directly and a stale
// string cache would wrongly suppress the next real update.
function setStatus(msg: string, level: 'info' | 'warn' | 'error' = 'info'): void {
  const el = $('status');
  const cls = `hud ${level === 'info' ? '' : level}`.trim();
  if (el.textContent === msg && el.className === cls) return;
  el.textContent = msg;
  el.className = cls;
}

function wsURL(roomId: string | null, spectate: boolean): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams();
  if (roomId) params.set('room', roomId);
  if (spectate) params.set('spectate', '1');
  const q = params.toString();
  return `${proto}//${location.host}/ws${q ? `?${q}` : ''}`;
}

const stage = $('stage') as HTMLCanvasElement;
const renderer = new Renderer(stage);
// Gunsight auto-hide-at-max preference. Off by default — matches the user's
// established "always visible" experience. Persisted via localStorage so a
// player who turns it on doesn't have to repeat the choice every session.
// Mirrors WinBolo's `autoHideGunsight` (tank.c:731-762); toggled by the G
// hotkey below.
const GUNSIGHT_HIDE_STORAGE_KEY = 'bolo2k_gunsight_hide_at_max';
renderer.hideGunsightAtMax = localStorage.getItem(GUNSIGHT_HIDE_STORAGE_KEY) === '1';
const minimapCanvas = document.querySelector('#minimap canvas') as HTMLCanvasElement;
const minimap = new Minimap(minimapCanvas);
const rightColumn = new RightColumn(
  document.querySelector('#tally .bolo-panel-inner') as HTMLElement,
  document.querySelector('#bars .bolo-panel-inner') as HTMLElement,
);
const keyboard = new Keyboard();
keyboard.attach(window);
const buildUI = new BuildUI(stage, $('status'));
const actionSidebar = new ActionSidebar($('action-sidebar'), buildUI);
const middleColumn = new MiddleColumn(
  document.querySelector('#pill-status-grid canvas') as HTMLCanvasElement,
  document.getElementById('base-status-grid-canvas') as HTMLCanvasElement,
);
const alliancePanel = new AlliancePanel($('ally-panel'));
const chatUI = new ChatUI($('chat-panel'));
const newsWireEl = document.querySelector('#news-wire .bolo-panel-inner') as HTMLElement;
const newsWire = new NewsWire(newsWireEl);
// Gameplay-event detector — diffs snapshots and emits WinBolo-style
// capture/lost-builder/quit lines onto the wire. See wireEvents.ts.
const wireEvents = new WireEvents({ add: (text) => newsWire.add(text) });
new Keyhint($('keyhint'));

// Gunsight auto-hide-at-max toggle. WinBolo `autoHideGunsight` preference
// rebound to a single hotkey: G flips between "always shown" (the modern
// QoL default) and "hidden when range is maxed". The latter mirrors Mac
// Bolo's behaviour where a player who wanted the screen uncluttered set
// range to max and got back a clean view. Setting persists in localStorage.
window.addEventListener('keydown', (e) => {
  if (document.activeElement instanceof HTMLInputElement) return;
  if (document.activeElement instanceof HTMLTextAreaElement) return;
  if (e.code !== 'KeyG' || e.ctrlKey || e.metaKey || e.altKey) return;
  renderer.hideGunsightAtMax = !renderer.hideGunsightAtMax;
  localStorage.setItem(GUNSIGHT_HIDE_STORAGE_KEY, renderer.hideGunsightAtMax ? '1' : '0');
  setStatus(
    renderer.hideGunsightAtMax
      ? 'gunsight: auto-hide at max range ON'
      : 'gunsight: always visible',
    'info',
  );
  e.preventDefault();
});

// Pillview (Cheshire MacBolo Instructions "Remote Views" + Brain.h
// `pillview`). V cycles through pills owned by you OR an active
// mutual-pact ally — tank → pill0 → pill1 → … → tank. Shift+V is the
// emergency snap-back to tank view. Server validates each request; if
// the chosen pill is destroyed / captured / picked up / alliance
// broken, the next snapshot's viewPillId echo is 0 and the client UI
// reverts naturally.
window.addEventListener('keydown', (e) => {
  if (document.activeElement instanceof HTMLInputElement) return;
  if (document.activeElement instanceof HTMLTextAreaElement) return;
  if (e.code !== 'KeyV' || e.ctrlKey || e.metaKey || e.altKey) return;
  if (!conn || !state || state.myOwner < 0) return;
  // Local non-null binding — the lambdas below run later and the closure
  // doesn't preserve TS's narrowing on the module-level `state`.
  const st = state;
  if (e.shiftKey) {
    if (st.viewPillId !== 0) conn.setView(0);
    e.preventDefault();
    return;
  }
  // Cycle: collect alive on-map pills the player is allowed to view —
  // own + active mutual-pact ally. Sort by id for stable ordering.
  const eligible = st.pillboxes
    .filter((p) => p.armour > 0 && p.carriedBy === 0 && isFriendlyToMe(st, p.owner))
    .sort((a, b) => a.id - b.id);
  if (eligible.length === 0) return; // nothing to view through
  const currentIdx = eligible.findIndex((p) => p.id === st.viewPillId);
  // currentIdx === -1 means we're on tank view OR the active pill just
  // dropped off the list (ally pact broke, etc). Either way, jump to
  // the first eligible pill. currentIdx >= 0 means cycle to the next,
  // wrapping back to tank after the last pill.
  let next: number;
  if (currentIdx < 0) next = eligible[0]!.id;
  else if (currentIdx === eligible.length - 1) next = 0;
  else next = eligible[currentIdx + 1]!.id;
  conn.setView(next);
  e.preventDefault();
});
window.addEventListener('keydown', (e) => chatUI.handleGlobalKey(e));

const audio = new Audio();
// Browsers create AudioContexts in 'suspended' state until a user gesture.
// Wire enable() to the first click and key press; either does the trick,
// after which the listeners self-remove.
const enableAudio = () => {
  audio.enable();
  window.removeEventListener('pointerdown', enableAudio);
  window.removeEventListener('keydown', enableAudio);
};
window.addEventListener('pointerdown', enableAudio);
window.addEventListener('keydown', enableAudio);

// Snapshot diffing for sound triggers — explosions are entities that linger
// for several ticks of animation, so we dedupe by id to fire the explosion
// SFX exactly once per new explosion. Shell muzzle SFX is driven by
// state.soundEvents (see playSnapshotSounds below) so no per-shell dedup
// here. Reset on (re)welcome so a reconnect doesn't replay every in-flight
// explosion.
let heardExplosionIds = new Set<number>();
// Scratch set for the two-set swap in playSnapshotSounds — reused across
// snapshots (cleared, never reallocated) so the 25 Hz snapshot path doesn't
// allocate a Set per call.
let nextHeardExplosionIds = new Set<number>();
let firstSnapshotProcessed = false;
// Last-seen ammo totals on the local tank — diff against each snapshot to
// detect a water-drain pulse (server decrements shells+mines on River tiles
// every TANK_WATER_TICKS) and play the bubbles sample.
let lastSelfShells = -1;
let lastSelfMines = -1;

// Canvas size is driven by the CSS Grid slot, not the window. Observe the
// stage-clip wrapper (the element actually pinned to the grid cell) — the
// canvas inside is intentionally 1 CSS pixel larger for sub-pixel scroll
// smoothing, so observing it directly would feed the renderer a size that's
// off by one.
const stageClip = $('stage-clip') as HTMLDivElement;
function fitCanvas(): void {
  const rect = stageClip.getBoundingClientRect();
  if (rect.width > 0 && rect.height > 0) {
    renderer.resize(rect.width, rect.height);
  }
}
fitCanvas();
const stageObserver = new ResizeObserver(() => fitCanvas());
stageObserver.observe(stageClip);

// Kick off sprite loading in parallel with the lobby — when they arrive,
// invalidate the terrain cache so the next frame repaints with sprites.
loadSprites()
  .then(() => renderer.invalidateTerrainCache())
  .catch((err) => newsWire.add(`sprite load failed: ${err.message}`));

let state: ClientState | null = null;
let lastInputBits = -1;
const interpolator = new Interpolator();
const predictor = new Predictor();

// Spectator camera: free-pan tile coords driven by WASD/arrow input each
// frame (since spectators don't own a tank for the camera to follow).
// Initialised to map centre on first spectator frame; null while a player
// is in-game (the renderer falls back to tank-tracking). Persists across
// snapshots so panning is smooth.
let spectatorCam: { x: number; y: number } | null = null;
const SPECTATOR_PAN_TILES_PER_FRAME = 0.5;

// Lazily set after the lobby resolves; the render loop deals gracefully with
// `null` (no state yet) so it can run uninterrupted from page load.
let conn: Connection | null = null;

// Action auto-substitution before sending to server. Two rules apply:
//
//   1. Forest click with Road / Wall / Pillbox / Mine armed → substitute
//      Farm. The user's intent is unambiguous (none of those actions work on
//      a forest tile), and the action stays armed so the next click on the
//      cleared tile places the original action. Farm itself is the no-op case.
//      See DECISIONS.md "Smart-fallback: forest click auto-farms".
//
//   2. River click with Wall (Building) armed → substitute Boat (places a
//      wooden boat tile, costs TREE_COST_BOAT). Mirrors Cheshire/WinBolo
//      lgm.c:386-394 where LGM_BUILDING_REQUEST on RIVER auto-becomes
//      LGM_BOAT_REQUEST. The Boat action is no longer user-armable directly;
//      this substitution is the only way to issue it.
//
// Note: Road on River is NOT substituted — that's the cheap permanent
// road-bridge mechanic ([sim/lgm.ts] canRoad allows River). Wall and Road
// give players two different crossing options.
function autoSubstituteAction(
  build: { action: BuildActionCode; targetX: number; targetY: number },
  st: ClientState,
): { action: BuildActionCode; targetX: number; targetY: number } {
  const idx = build.targetY * MAP_SIZE + build.targetX;
  const terr = (st.terrain[idx] ?? 0) & TERRAIN_MASK;
  if (
    terr === Terrain.Forest &&
    (build.action === BuildAction.Road ||
      build.action === BuildAction.Building ||
      build.action === BuildAction.Pillbox ||
      build.action === BuildAction.Mine)
  ) {
    return { ...build, action: BuildAction.Farm };
  }
  if (terr === Terrain.River && build.action === BuildAction.Building) {
    return { ...build, action: BuildAction.Boat };
  }
  return build;
}

// Pre-flight check on a build click — returns a short human-readable reason
// when the request would be silently rejected by the server (the canonical
// gate is `reserveJob` in sim/lgm.ts), or null when it should be sent
// through. UX courtesy so the player isn't left guessing why nothing
// happened. Only covers actions whose failure modes aren't obvious in-world
// (Pillbox, Boat); Farm/Road/Building/Mine fall through unchecked.
function buildRejection(
  build: { action: BuildActionCode; targetX: number; targetY: number },
  st: ClientState,
): string | null {
  const me = findMyTank(st);
  if (!me) return null;
  if (build.action === BuildAction.Boat) {
    if (me.trees < TREE_COST_BOAT) {
      return `not enough trees to build boat (need ${TREE_COST_BOAT}, have ${me.trees})`;
    }
    return null;
  }
  if (build.action !== BuildAction.Pillbox) return null;
  const idx = build.targetY * MAP_SIZE + build.targetX;
  const terr = (st.terrain[idx] ?? 0) & TERRAIN_MASK;
  const existing = st.pillboxes.find(
    (p) => p.x === build.targetX && p.y === build.targetY,
  );
  if (existing) {
    if (existing.armour >= PILL_MAX_HEALTH) return 'pillbox already at full health';
    if (me.trees < TREE_COST_REPAIR_PILL) {
      return `not enough trees to repair (need ${TREE_COST_REPAIR_PILL}, have ${me.trees})`;
    }
    return null;
  }
  // Placing a NEW pill — terrain check mirrors `canPillbox` in sim/lgm.ts.
  const placeable =
    terr === Terrain.Swamp ||
    terr === Terrain.Crater ||
    terr === Terrain.Road ||
    terr === Terrain.Rubble ||
    terr === Terrain.Grass;
  if (!placeable) return 'cannot place a pillbox on that terrain';
  if (me.carriedPills < 1) {
    return 'no pillbox to place — drive over a destroyed pillbox to pick one up';
  }
  if (me.trees < TREE_COST_NEW_PILL) {
    return `not enough trees to place pillbox (need ${TREE_COST_NEW_PILL}, have ${me.trees})`;
  }
  return null;
}

// Diff the snapshot against what we've already heard and fire one sound per
// new shell / explosion. Listener position = local tank (full-volume sounds
// near you, distance-attenuated for far-off battles); spectators hear all
// sounds at full volume.
function playSnapshotSounds(snap: import('@bolo2k/shared').S2CSnapshot, st: ClientState): void {
  const me = findMyTank(st);
  const listener = me ? { x: me.x, y: me.y } : null;

  // First snapshot after welcome: seed the heard set without playing — any
  // explosions in flight at join time aren't ours and we shouldn't hear
  // "pop pop pop" of historical events. Shells don't need a seed because
  // muzzle SFX comes from state.soundEvents (cleared by the server after
  // every broadcast), not from diffing the shells array.
  if (!firstSnapshotProcessed) {
    for (const e of snap.tankExplosions) heardExplosionIds.add(e.id);
    if (me) {
      lastSelfShells = me.shells;
      lastSelfMines = me.mines;
    }
    firstSnapshotProcessed = true;
    return;
  }

  // Bubbles: play once per water-drain tick. The server decrements shells
  // and mines together every TANK_WATER_TICKS while the tank is on River
  // without a boat — observe by diffing against the last snapshot. Skip
  // when the tank just fired (shells--) or just laid a mine (mines--) so
  // those legitimate decrements don't fake a drain.
  if (me && me.alive && me.inBoatId === 0 && lastSelfShells >= 0 && lastSelfMines >= 0) {
    const shellsDropped = me.shells < lastSelfShells;
    const minesDropped = me.mines < lastSelfMines;
    if (shellsDropped && minesDropped) {
      audio.play('bubbles', me.x, me.y, listener);
    }
  }
  if (me) {
    lastSelfShells = me.shells;
    lastSelfMines = me.mines;
  }

  // Shell muzzle SFX is fired from sim/shells.ts and sim/pillboxes.ts via
  // emitSound('shellFire'); see the snap.soundEvents loop below. We used to
  // ALSO play shellFire by diffing snap.shells against a heardShellIds set,
  // which double-fired for every shot (one play from the diff, one from the
  // soundEvent). soundEvents wins because it also reaches the client when a
  // close-range shell is destroyed before its first snapshot — that path is
  // now visible thanks to the dyingTicks deferral in sim/shells.ts, but
  // sound timing should still come from the authoritative server emit.
  // Two-set swap: build the next heard set from the snapshot's live
  // explosions (playing each id we haven't heard), then swap it in. Ends in
  // the same state as the old add-then-prune loop — heard === live — but
  // reuses two persistent Sets instead of allocating one per snapshot.
  nextHeardExplosionIds.clear();
  for (const e of snap.tankExplosions) {
    if (!heardExplosionIds.has(e.id)) {
      audio.play(e.kind === 'big' ? 'explosionBig' : 'explosionSmall', e.x, e.y, listener);
    }
    nextHeardExplosionIds.add(e.id);
  }
  [heardExplosionIds, nextHeardExplosionIds] = [nextHeardExplosionIds, heardExplosionIds];

  // One-shot soundEvents are sent fresh in every snapshot (server clears
  // its array after each broadcast). No need to dedupe by id — just play
  // each one. Skip events too far away to be heard (the audio module also
  // gates on MAX_AUDIBLE_TILES, but we still iterate all of them so the
  // listener position is computed once).
  for (const ev of snap.soundEvents) {
    audio.play(ev.kind, ev.x, ev.y, listener);
  }
}

(async () => {
  let lobbyResult: Awaited<ReturnType<typeof showLobby>>;
  try {
    lobbyResult = await showLobby(document.body);
  } catch (err) {
    newsWire.add(`lobby failed: ${String(err)}`);
    return;
  }
  const { roomId, name, spectate, bots: queuedBots } = lobbyResult;
  newsWire.add(`connecting to room ${roomId}…`);
  conn = new Connection(
    wsURL(roomId, spectate),
    {
      onWelcome: (welcome) => {
        state = initFromWelcome(welcome);
        chatUI.setSpectator(welcome.owner < 0);
        // Reset the heard-event set so a reconnect's first snapshot doesn't
        // suddenly replay every explosion that was already in flight.
        heardExplosionIds = new Set();
        firstSnapshotProcessed = false;
        // Re-seed the wire-event detector: the first post-welcome snapshot
        // is the baseline, not a diff, so we don't replay ownerships as
        // "captures" on reconnect / rejoin.
        wireEvents.reset();
        lastSelfShells = -1;
        lastSelfMines = -1;
        if (welcome.owner < 0) {
          newsWire.add(`spectating "${welcome.mapName}"`);
          newsWire.add('WASD or arrow keys pan the camera');
        } else {
          const who = name ? `"${name}"` : `player ${welcome.owner}`;
          newsWire.add(`connected as ${who} on "${welcome.mapName}"`);
        }
        // Spawn the bots the player queued in the lobby. Fire-and-forget;
        // the server applies them as ordinary addBot requests. Skipped for
        // spectators (queue is empty in that case anyway).
        if (welcome.owner >= 0 && queuedBots.length > 0 && conn) {
          for (const p of queuedBots) conn.addBot(p);
          newsWire.add(`spawned ${queuedBots.length} bot${queuedBots.length > 1 ? 's' : ''}`);
        }
      },
      onSnapshot: (snap) => {
        if (!state) return;
        applySnapshot(state, snap);
        interpolator.recordSnapshot(state);
        predictor.reconcile(state);
        // (Alliance panel re-renders in the per-frame draw loop, not here.)
        chatUI.setPlayerNames(state.playerNames);
        playSnapshotSounds(snap, state);
        wireEvents.observe(snap);
      },
      onMessage: (msg) => {
        if (msg.type === 's2c.error') {
          newsWire.add(`server: ${msg.message}`);
        } else if (msg.type === 's2c.chat') {
          chatUI.applyChatMessage(msg);
        }
      },
      onClose: (code, reason) => {
        newsWire.add(`disconnected (${code}${reason ? ` ${reason}` : ''})`);
      },
      onError: (err) => {
        newsWire.add(`network error: ${err.message}`);
      },
      onReconnecting: (attempt, delayMs) => {
        newsWire.add(`reconnecting (attempt ${attempt} in ${delayMs} ms)…`);
      },
      onReconnected: () => {
        newsWire.add('reconnected');
        lastInputBits = -1;
      },
      onGiveUp: () => {
        newsWire.add('disconnected — refresh to reconnect');
      },
    },
    undefined,
    { name },
  );
  alliancePanel.attachConnection(conn);
  chatUI.attach(conn, keyboard, (msg) => newsWire.add(msg));
})();

function frame(): void {
  if (state) {
    const bits = keyboard.bitfield();

    // Run client-side prediction with the current input bitfield. The
    // predictor advances one sim tick per TICK_MS of wall-clock and stays in
    // sync with the server through reconcile() on every snapshot.
    const predictedMe = predictor.predict(state, { keys: bits });

    // Spectators repurpose WASD/arrows as free camera pan (they have no
    // tank to drive). Initialised lazily on the first spectator frame so the
    // map centre is the starting view; subsequent frames just integrate the
    // input bits. Cleared back to null when a player joins so the renderer
    // falls back to tank-tracking.
    if (state.myOwner < 0) {
      if (!spectatorCam) spectatorCam = { x: MAP_SIZE / 2, y: MAP_SIZE / 2 };
      if (bits & TankKey.TurnLeft) spectatorCam.x -= SPECTATOR_PAN_TILES_PER_FRAME;
      if (bits & TankKey.TurnRight) spectatorCam.x += SPECTATOR_PAN_TILES_PER_FRAME;
      if (bits & TankKey.Faster) spectatorCam.y -= SPECTATOR_PAN_TILES_PER_FRAME;
      if (bits & TankKey.Slower) spectatorCam.y += SPECTATOR_PAN_TILES_PER_FRAME;
      spectatorCam.x = Math.max(0, Math.min(MAP_SIZE, spectatorCam.x));
      spectatorCam.y = Math.max(0, Math.min(MAP_SIZE, spectatorCam.y));
    } else if (spectatorCam) {
      spectatorCam = null;
    }

    // Pillview camera override: when the server's last snapshot put us
    // on a friendly pillbox, recenter the camera on its tile coords. The
    // renderer treats this the same as the spectator pan override — same
    // mechanism, different driver. Falls back to tank tracking the moment
    // the snapshot echoes viewPillId=0 (pill destroyed/captured/etc).
    let cameraOverride: { x: number; y: number } | null = spectatorCam;
    if (cameraOverride === null && state.viewPillId > 0) {
      const targetId = state.viewPillId;
      const pill = state.pillboxes.find((p) => p.id === targetId);
      if (pill) cameraOverride = { x: pill.x + 0.5, y: pill.y + 0.5 };
    }

    const cam = renderer.cameraTile();
    buildUI.setCamera(cam.x, cam.y);
    renderer.draw(state, interpolator, (ctx) => buildUI.draw(ctx), predictedMe, cameraOverride);
    minimap.draw(state);
    actionSidebar.draw(buildUI);
    middleColumn.draw(state);
    rightColumn.draw(state);
    alliancePanel.draw(state);

    // One-shot build command: emit if the player just clicked while armed,
    // BUT only when the LGM is idle in the tank. Sending while the LGM is
    // walking / building was the cause of "click frequently does nothing" —
    // the server silently dropped the input because lgm.ts:368 gates on
    // `lgm.status === InTank`. Holding the click here lets it fire the
    // moment the LGM returns home (latest-click-wins; subsequent clicks
    // overwrite the queued one in BuildUI).
    const me = findMyTank(state);
    const lgmFree = !!me && me.lgmStatus === LGMStatus.InTank;
    if (me && buildUI.hasPending() && !lgmFree) {
      // Surface a one-shot status hint so the user understands the wait.
      // Cleared/refreshed each frame the click is held; the action-status
      // line is the natural place since the player is already looking at it.
      setStatus('LGM busy — your click is queued', 'info');
    }
    const build = lgmFree ? buildUI.tryEmit() : null;
    if (build && me && conn) {
      const adjusted = autoSubstituteAction(build, state);
      if (adjusted !== build) {
        if (adjusted.action === BuildAction.Farm) {
          setStatus('forest tile — auto-farming first; click again after to build', 'info');
        } else if (adjusted.action === BuildAction.Boat) {
          setStatus('river tile — building boat (wall on water)', 'info');
        }
      }
      // Pre-flight check on Pillbox: surface a helpful reason via the news
      // wire when the server would silently reject (no carried pill, no
      // trees, full HP, wrong terrain). Other actions aren't checked here
      // because their failure modes are more obvious in-world.
      const rejection = buildRejection(adjusted, state);
      if (rejection) {
        newsWire.add(rejection);
      } else {
        conn.sendInput(bits, adjusted);
        lastInputBits = -1; // re-send keys next frame too
      }
    } else if (bits !== lastInputBits && me && conn) {
      // Send input only when the bitfield changes — the server applies the
      // latest known input on every tick anyway, so we save bandwidth.
      conn.sendInput(bits);
      lastInputBits = bits;
    }
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);
